import { useEffect } from "react"
import PropTypes from "prop-types"
import { capitalize, noop, slice } from "lodash"
import { Factor } from "components/factor-view"
import { ANNUALIZED_VOLATILITY_THRESHOLD, percentageFormatter, numberFormatter } from "helpers/factor-view"
import ValueWithTooltip from "components/factor-view/value-with-tooltip"
import AnalyzeFactorsContent from "components/analyze-factors-content"
import { DATE, LIST_ITEM_TEXT_OPTION } from "helpers/shapes"
import { METRIC_TYPES } from "helpers/assets/constants"
import { assetDollarFormatter } from "helpers/formatters/numbers"

export default function AnalyzeFactorsPerformance({
  selectedFactorCategory,
  totalRow,
  factors,
  categories,
  setFactorCategory,
  isActive,
  startDate,
  endDate,
  status,
  setLoaded = noop,
  filtersVisible,
  metricType,
  setMetricType,
  metricTypeOptions,
  displayMetricSelector,
}) {
  useEffect(() => {
    setLoaded(status === "ok")
    // on unmount, set loaded back to false
    return () => setLoaded(false)
  }, [status, setLoaded])

  const titlePrefix = `${capitalize(selectedFactorCategory)}${selectedFactorCategory !== "factors" ? " factors" : ""}`
  const metricTypeSymbol = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? "$" : "%"
  const metricFormatter = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? assetDollarFormatter : percentageFormatter
  const columns = [{
    id: "label",
    name: "Factor",
    render: factor => <Factor factor={factor} />,
  },
  {
    id: "cumulativeReturn",
    name: `Cumulative Return\u00A0${metricTypeSymbol}`,
    formatter: percentageFormatter,
    numeric: true,
    defaultSort: "desc",
    render: ({ cumulativeReturn, annualizedReturns }) => <ValueWithTooltip value={{ value: metricFormatter(cumulativeReturn), tooltip: `Annualized return %: ${numberFormatter(annualizedReturns)}` }} />,
  },
  {
    id: "annualizedVolatility",
    name: "Ann Vol\u00A0%",
    formatter: numberFormatter,
    numeric: true,
  },
  {
    id: "informationRatio",
    name: !isActive ? "Sharpe" : "Information ratio",
    numeric: true,
    sortGetter: ({ annualizedVolatility, informationRatio }) => annualizedVolatility < ANNUALIZED_VOLATILITY_THRESHOLD ? NaN : informationRatio,
    render: ({ annualizedVolatility, informationRatio }) => annualizedVolatility < ANNUALIZED_VOLATILITY_THRESHOLD ? "-" : numberFormatter(informationRatio),
  }]

  const valueColumns = slice(columns, 1)
  return <AnalyzeFactorsContent {...{
    type: "performance",
    titlePrefix,
    totalRow,
    columns,
    totalColumns: valueColumns,
    categoriesColumns: valueColumns,
    factors,
    categories,
    selectedFactorCategory,
    setFactorCategory,
    startDate,
    endDate,
    isActive,
    filtersVisible,
    metricType,
    setMetricType,
    metricTypeOptions,
    displayMetricSelector,
  }}
  />
}

AnalyzeFactorsPerformance.propTypes = {
  selectedView: PropTypes.string.isRequired,
  selectedFactorCategory: PropTypes.string.isRequired,
  totalRow: PropTypes.array.isRequired,
  factors: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  setFactorCategory: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  startDate: DATE,
  endDate: DATE,
  setLoaded: PropTypes.func,
  status: PropTypes.string.isRequired,
  filtersVisible: PropTypes.bool,
  metricType: PropTypes.string,
  setMetricType: PropTypes.func,
  metricTypeOptions: PropTypes.arrayOf(LIST_ITEM_TEXT_OPTION),
  displayMetricSelector: PropTypes.bool,
}
